import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Full-Service Offer";
const h1Text = "Your strategic and operational partner for a comprehensive full-service package in the DACH region. As your extended arm, we manage your entire local operations so you can keep your focus on your home markets.";
const heroImage = "https://net-basis.net/Bilder/ict-full-service-business-development-management.webp";
const heroAlt = "Businessman uses a digital dashboard for a centrally managed ICT full-service offer.";
const heroClaimLine1 = "Everything from";
const heroClaimLine2 = "one source!";
const benefitsTitle = "How you benefit from our __Full-Service Offer__ expertise:";

const p1Text = "NetBasis International acts for foreign companies as a local pillar in their new markets. From a broad portfolio of services, our manufacturing partners can select individual services tailored to their company-specific requirements.";
const p2Text = "The most comprehensive approach to entering a new market, however, and thus the best way to ensure successful work, is our full-service package. In this way, our manufacturing partners keep their heads free for business in their home markets and leave the complete management of the new markets to NetBasis International, naturally in close cooperation.";
const p3Text = "With a full-service contract, NetBasis International takes over the entire local business as well as the planning, execution, and monitoring of all upcoming tasks.";

const p1Parts = p1Text.split(COMPANY_NAME);
const p2Parts = p2Text.split(COMPANY_NAME);
const p3Parts = p3Text.split(COMPANY_NAME);

const sections = [
  {
    title: "1. All-inclusive",
    items: [
      "Postal address and complete on-site office service",
      "Technical and commercial evaluation of your products",
      "Creation of all necessary commercial and technical documents",
      "Intensive and well-founded competitive analysis in the target market",
      "Strategic pricing and market positioning",
      "Marketing of selected or all products and solutions as your importer",
      "Official registration or notification of hardware products with authorities such as the Stiftung EAR",
      "Reliable technical support according to clearly defined service levels",
      "We at NetBasis International develop your business as your local representation",
    ],
  },
  {
    title: "2. Hands-on",
    items: [
      "A central point of contact for your concerns as well as for your local customers in English and German",
      "Complete sales, targeted marketing, and support in your name directly in the market",
    ],
  },
  {
    title: "3. Fast",
    items: [
      "No long startup phase – with us, you are immediately ready for action",
      "Direct focus on the actual, productive work on-site",
      "No delay in contacting potential customers or qualified sales partners",
      "Uncomplicated local customer support, customer visits, and professional trade fair participation",
    ],
  },
  {
    title: "4. Close to the customer",
    items: [
      "Present on-site without language or cultural barriers",
      "Professional, experienced, reliable, and customer-oriented public image",
      "High entrepreneurial agility and extremely short response times",
    ],
  },
  {
    title: "5. Cost-effective",
    items: [
      "No costs for an expensive international office and staff – minimal fixed costs without high investments",
      "Clear and sustainable monthly flat fees plus success commission according to the partner agreement",
    ],
  },
];

const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

export default function FullServicePackageEnPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Full-Service Offer | NetBasis International"
        description="From market analysis to sales: Our modular complete package for your turnkey international market success."
        ogUrl="https://net-basis.net/en/services/business-development/full-service-offer"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-end pt-[12%] md:pt-[4%] lg:pt-[4%] pr-[8%] md:pr-[4%] pointer-events-none">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-right leading-[1.15] md:leading-[1.5] animate-[presenceTextSlide_12s_ease-in-out_infinite]"
                  style={{
                    fontFamily: '"Open Sans", sans-serif',
                  }}
                >
                  <span className="block md:inline">{heroClaimLine1}</span>
                  {heroClaimLine2 && <span className="hidden md:inline"> </span>}
                  {heroClaimLine2 && <span className="block md:inline">{heroClaimLine2}</span>}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="font-semibold text-foreground-950 text-sm md:text-base mb-4">
              {h1Text}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {p1Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p1Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>
                {p2Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p2Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>
                {p3Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p3Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
            </div>

            {/* Green heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            {/* Sections with green sub-headings and bullet lists */}
            <div className="space-y-5 text-foreground-950 leading-relaxed text-sm md:text-base">
              {sections.map((section, idx) => (
                <div key={idx}>
                  <h3 className="text-primary-600 font-bold text-sm md:text-base mb-2">
                    {section.title}
                  </h3>
                  <ul className="list-disc pl-5 space-y-1">
                    {section.items.map((item, j) => (
                      <li key={j}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}